// src/pages/OffStreetMasterData.jsx
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Plus, Search, SlidersHorizontal, ChevronDown, ChevronRight,
  Globe, Building2, MapPin, Navigation, CircleDot, MapPinned,
  Trash2, Loader2, Copy,
} from 'lucide-react';
import { apiCall } from '@/api/apiCall';
import { useTheme } from '@/components/theme-provider';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/common/DataTable';
import useDebounce from '@/hooks/useDebounce';
import LoadingComponent from '@/components/common/LoadingComponent';
import ReactSelect from '@/components/common/ReactSelect';
import PolygonMapEditor, { duplicateDeviceApi, createDeviceApi } from '@/components/child/PolygonMapEditor';
import { getDeviceColor, getDeviceTypeName } from '@/components/child/offStreetDeviceVisuals';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

/* ============================================================================
 * SECTION 1 — DATA HELPERS (normalization, composite keys)
 * ==========================================================================*/

const isUnknownGate = (gate) => {
  if (!gate) return false;
  const id = String(gate.gate_id ?? gate.gate ?? '').trim().toUpperCase();
  const name = String(gate.gate_name ?? '').trim().toUpperCase();
  return id === 'UNKNOWN' || (!id && name === 'UNKNOWN');
};
const getGateDisplayName = (gate) =>
  isUnknownGate(gate) ? 'General' : gate?.gate_name || gate?.gate_id || gate?.gate || 'Gate';

const hasPolygonSet = (raw) =>
  raw?.polygon_set ? raw.polygon_set === 'Y' : !!(Array.isArray(raw?.coordinates) && raw.coordinates.length);

const getCountryOps = (countryObj) => {
  if (!countryObj) return [];
  if (Array.isArray(countryObj.cities) && countryObj.cities.length) {
    return countryObj.cities.flatMap((city) =>
      (city.ops || []).map((op) => ({ ...op, city_cd: city.city_cd, city_name: city.city_name }))
    );
  }
  return countryObj.ops || [];
};
const getTopLevelCountries = (topLevel = []) =>
  topLevel.flatMap((item) => (item.children?.length ? item.children : item.ops || item.cities ? [item] : []));

const getCountryCities = (countryObj) =>
  Array.isArray(countryObj?.cities) ? countryObj.cities : [];

const buildKey = (...parts) => parts.map((p) => (p === undefined || p === null || p === '' ? 'x' : p)).join('/');

const collectDeviceTypes = (topLevel = []) => {
  const set = new Set();
  getTopLevelCountries(topLevel).forEach((c) =>
    getCountryOps(c).forEach((op) =>
      (op.locations || []).forEach((loc) =>
        (loc.gates || []).forEach((gate) =>
          (gate.devices || []).forEach((d) => { if (d.device_type) set.add(String(d.device_type).toUpperCase()); })))));
  return Array.from(set).sort();
};

function normalizeHierarchy(topLevel = []) {
  const rows = [];
  getTopLevelCountries(topLevel).forEach((countryObj) => {
    const countryId = buildKey(countryObj.country);
    const ops = getCountryOps(countryObj);
    const citiesByCd = new Map(getCountryCities(countryObj).map((c) => [c.city_cd, c]));
    const cityMap = new Map();
    ops.forEach((op) => {
      const cityCd = op.city_cd || op.ops;
      const cityName = op.city_name || op.ops_name;
      if (!cityMap.has(cityCd)) {
        cityMap.set(cityCd, { ...(citiesByCd.get(cityCd) || {}), city_cd: cityCd, city_name: cityName, opsList: [] });
      }
      cityMap.get(cityCd).opsList.push(op);
    });

    let countryLocations = 0, countryGates = 0, countryDevices = 0;

    cityMap.forEach((cityGroup) => {
      const cityId = buildKey(countryId, cityGroup.city_cd);
      let cityLocations = 0, cityGates = 0, cityDevices = 0;

      cityGroup.opsList.forEach((op) => {
        const opsId = buildKey(cityId, op.ops);
        let opsGates = 0, opsDevices = 0;
        const locations = op.locations || [];

        locations.forEach((loc) => {
          const locId = buildKey(opsId, loc.location);
          let locDevices = 0;
          const gates = loc.gates || [];

          gates.forEach((gate, gi) => {
            const gateIdRaw = gate.gate_id ?? gate.gate ?? 'General';
            const gateId = buildKey(locId, gateIdRaw, gi);
            const devices = gate.devices || [];

            devices.forEach((device, di) => {
              const hasCoords = !!(device.latitude && device.longitude && !isNaN(parseFloat(device.latitude)) && !isNaN(parseFloat(device.longitude)));
              rows.push({
                level: 'DEVICE',
                id: buildKey(gateId, device.device_id, device.device_name, di),
                parentId: gateId,
                name: device.device_name || device.device_id || 'Device',
                code: device.device_id ?? '',
                rowId: device.row_id ?? null,
                deviceType: device.device_type || null,
                coordStatus: hasCoords ? 'Coordinates Set' : 'Coordinates Missing',
                lat: hasCoords ? parseFloat(device.latitude) : null,
                lng: hasCoords ? parseFloat(device.longitude) : null,
                childCount: 0,
                raw: device,
                path: { country: countryObj, city: cityGroup, op, loc, gate, device },
              });
              locDevices += 1; opsDevices += 1; cityDevices += 1; countryDevices += 1;
            });

            rows.push({
              level: 'GATE',
              id: gateId,
              parentId: locId,
              name: getGateDisplayName(gate),
              code: isUnknownGate(gate) ? 'General' : gate.gate_id ?? '',
              isUnknown: isUnknownGate(gate),
              polygonStatus: hasPolygonSet(gate) ? 'Polygon Set' : 'Polygon Not Set',
              childCount: devices.length,
              raw: gate,
              path: { country: countryObj, city: cityGroup, op, loc, gate },
            });
            opsGates += 1; cityGates += 1; countryGates += 1;
          });

          rows.push({
            level: 'LOCATION',
            id: locId,
            parentId: opsId,
            name: loc.location || 'Location',
            code: loc.location ?? '',
            polygonStatus: hasPolygonSet(loc) ? 'Polygon Set' : 'Polygon Not Set',
            projectStatus: loc.project_status || null,
            childCount: gates.length,
            deviceCount: locDevices,
            raw: loc,
            path: { country: countryObj, city: cityGroup, op, loc },
          });
        });

        rows.push({
          level: 'OPS',
          id: opsId,
          parentId: cityId,
          name: op.ops_name || op.ops || 'Ops',
          code: op.ops ?? '',
          polygonStatus: hasPolygonSet(op) ? 'Polygon Set' : 'Polygon Not Set',
          projectStatus: op.project_status || null,
          childCount: locations.length,
          gateCount: opsGates,
          deviceCount: opsDevices,
          raw: op,
          path: { country: countryObj, city: cityGroup, op },
        });
        cityLocations += locations.length;
      });

      rows.push({
        level: 'CITY',
        id: cityId,
        parentId: countryId,
        name: cityGroup.city_name || cityGroup.city_cd || 'City',
        code: cityGroup.city_cd ?? '',
        polygonStatus: hasPolygonSet(cityGroup) ? 'Polygon Set' : 'Polygon Not Set',
        childCount: cityGroup.opsList.length,
        locationCount: cityLocations,
        gateCount: cityGates,
        deviceCount: cityDevices,
        raw: cityGroup,
        path: { country: countryObj, city: cityGroup },
      });
      countryLocations += cityLocations;
    });

    rows.push({
      level: 'COUNTRY',
      id: countryId,
      parentId: null,
      name: countryObj.country_name || countryObj.country || 'Country',
      code: countryObj.country ?? '',
      polygonStatus: hasPolygonSet(countryObj) ? 'Polygon Set' : 'Polygon Not Set',
      projectStatus: countryObj.project_status || null,
      childCount: cityMap.size,
      opsCount: ops.length,
      locationCount: countryLocations,
      gateCount: countryGates,
      deviceCount: countryDevices,
      raw: countryObj,
      path: { country: countryObj },
    });
  });
  return rows;
}

const indexByParent = (rows) => {
  const map = new Map();
  rows.forEach((r) => {
    const key = r.parentId ?? '__root__';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  });
  return map;
};

const buildBreadcrumb = (row, rowsById) => {
  const trail = [];
  let cur = row;
  while (cur) {
    trail.unshift({ level: cur.level, id: cur.id, name: cur.name });
    cur = cur.parentId ? rowsById.get(cur.parentId) : null;
  }
  return trail;
};

/* ============================================================================
 * SECTION 2 — SERVICE LAYER
 * ==========================================================================*/

async function fetchHierarchyData() {
  const res = await apiCall('get', '/cp-map/off-street', null, null, false, false);
  if (res?.success && res?.fields?.data) return res.fields.data;
  throw new Error(res?.message || 'Failed to load hierarchy');
}

async function createEntity(entityType, payload) {
  const res = await apiCall('post', `/cp-map/off-street/${entityType}`, payload, null, false, false);
  if (res?.success) return res.fields?.data ?? payload;
  throw new Error(res?.message || `Failed to create ${entityType}`);
}
async function updateEntityData(entityType, entityId, payload) {
  const res = await apiCall('put', `/cp-map/off-street/${entityType}/${entityId}`, payload, null, false, false);
  if (res?.success) return res.fields?.data ?? payload;
  throw new Error(res?.message || `Failed to update ${entityType}`);
}
async function deleteEntityData(entityType, entityId) {
  const res = await apiCall('delete', `/cp-map/off-street/${entityType}/${entityId}`, null, null, false, false);
  if (res?.success) return true;
  throw new Error(res?.message || `Failed to delete ${entityType}`);
}
async function deleteDeviceApi(rowId) {
  const res = await apiCall('post', '/off-street-device/delete', { row_id: rowId }, null, false, true);
  if (res?.success) return true;
  throw new Error(res?.message || 'Failed to delete device');
}
async function updateDevicesCoordinatesData(devices) {
  const payload = devices.map(({ rowId, latitude, longitude }) => ({ row_id: rowId, latitude, longitude }));
  const res = await apiCall('post', '/cp-map/off-street-device-update', payload, null, false, true);
  if (res?.success) return true;
  throw new Error(res?.message || 'Failed to update device coordinates');
}

/* ============================================================================
 * SECTION 4 — EXPANDABLE TREE TABLE
 * ==========================================================================*/

const LEVEL_META = {
  COUNTRY: { icon: Globe, addLabel: 'Add City' },
  CITY: { icon: Globe, addLabel: 'Add Ops' },
  OPS: { icon: Building2, addLabel: 'Add Location' },
  LOCATION: { icon: MapPin, addLabel: 'Add Gate' },
  GATE: { icon: Navigation, addLabel: 'Add Device' },
  DEVICE: { icon: CircleDot, addLabel: null },
};
const CHILD_COUNT_LABEL = {
  COUNTRY: (r) => `${r.childCount} cities · ${r.opsCount} ops · ${r.locationCount} loc · ${r.gateCount} gates · ${r.deviceCount} devices`,
  CITY: (r) => `${r.childCount} ops · ${r.locationCount} loc · ${r.gateCount} gates · ${r.deviceCount} devices`,
  OPS: (r) => `${r.childCount} locations · ${r.gateCount} gates · ${r.deviceCount} devices`,
  LOCATION: (r) => `${r.childCount} gates · ${r.deviceCount} devices`,
  GATE: (r) => `${r.childCount} devices`,
  DEVICE: () => null,
};

const HIERARCHY_LEVEL_ORDER = ['COUNTRY', 'CITY', 'OPS', 'LOCATION', 'GATE', 'DEVICE'];
const EXPAND_LEVEL_OPTIONS = [
  { level: 'CITY',    label: 'City' },
  { level: 'OPS',     label: 'Ops' },
  { level: 'LOCATION', label: 'Location' },
  { level: 'GATE',    label: 'Gate' },
  { level: 'DEVICE',  label: 'All' },
];
const COLLAPSE_LEVEL_OPTIONS = [
  { level: 'COUNTRY', label: 'Country' },
  { level: 'CITY',    label: 'City' },
  { level: 'OPS',     label: 'Ops' },
  { level: 'LOCATION', label: 'Location' },
  { level: 'GATE',    label: 'Gate' },
];

function TreeRow({ row, depth, rowsIndex, expanded, onToggle, onAction, highlightId }) {
  const meta = LEVEL_META[row.level];
  const Icon = meta.icon;
  const children = rowsIndex.get(row.id) || [];
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(row.id);
  const isDevice = row.level === 'DEVICE';
  const isHighlighted = row.id === highlightId;
  const cellStateBg = isHighlighted ? 'bg-primary/10' : 'bg-background group-hover:bg-muted-foreground/15';

  return (
    <>
      <tr id={`hrow-${row.id}`} className="group border-b border-border/60 transition-colors">
        <td className={`px-2 py-2 sticky left-0 transition-colors ${cellStateBg}`}>
          <div className="flex items-center gap-1.5" style={{ paddingLeft: depth * 18 }}>
            {hasChildren ? (
              <button onClick={() => onToggle(row.id)} className="text-muted-foreground hover:text-foreground flex-shrink-0">
                {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            ) : <span className="w-3.5 h-3.5 flex-shrink-0" />}
            <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: isDevice ? getDeviceColor(row.deviceType) : undefined }} />
            <span className="text-xs font-semibold truncate max-w-[220px] text-foreground">{row.name}</span>
            <span className="text-[9px] font-bold uppercase tracking-wide text-muted-foreground bg-secondary px-1.5 py-0.5 rounded-full flex-shrink-0">{row.level}</span>
          </div>
        </td>
        <td className={`px-2 py-2 text-[12px] transition-colors ${cellStateBg}`}>{row.code || '—'}</td>
        <td className={`px-2 py-2 text-[12px] transition-colors ${cellStateBg}`}>
          {isDevice ? (
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
              style={{ backgroundColor: `${getDeviceColor(row.deviceType)}1A`, color: getDeviceColor(row.deviceType) }}
            >
              {getDeviceTypeName(row.deviceType)}
            </span>
          ) : '—'}
        </td>
        <td className={`px-2 py-2 text-[12px] transition-colors ${cellStateBg}`}>{CHILD_COUNT_LABEL[row.level]?.(row) || '—'}</td>
        <td className={`px-2 py-2 text-[12px] transition-colors ${cellStateBg}`}>{isDevice ? (row.lat != null ? `${row.lat.toFixed(5)}, ${row.lng.toFixed(5)}` : '—') : '—'}</td>
        <td className={`px-2 py-2 transition-colors ${cellStateBg}`}>
          {isDevice ? (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${row.coordStatus === 'Coordinates Set' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>{row.coordStatus}</span>
          ) : (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${row.polygonStatus === 'Polygon Set' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-600'}`}>{row.polygonStatus}</span>
          )}
        </td>
        <td className={`px-2 py-2 text-[11px] transition-colors ${cellStateBg}`}>
          {row.projectStatus && String(row.projectStatus).toLowerCase() === 'inactive' ? (
            <span className="text-red-500 font-semibold">Inactive</span>
          ) : (
            <span className="text-emerald-500 font-semibold">Active</span>
          )}
        </td>
        <td className={`px-2 py-2 sticky right-0 transition-colors ${cellStateBg}`}>
          <div className="flex items-center gap-1 justify-end">
            {isDevice ? (
              <button title="Update Coordinates" onClick={() => onAction('coords', row)} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"><MapPinned className="w-3.5 h-3.5" /></button>
            ) : (
              <>
                {row.level === 'GATE' && (
                  <button title={meta.addLabel} onClick={() => onAction('addChild', row)} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"><Plus className="w-3.5 h-3.5" /></button>
                )}
                <button title="Manage Polygon" onClick={() => onAction('polygon', row)} className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground"><MapPinned className="w-3.5 h-3.5" /></button>
              </>
            )}
            {/* {isDevice && (
              <button title="Delete" onClick={() => onAction('delete', row)} className="p-1.5 rounded-md hover:bg-red-500/10 text-muted-foreground hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            )} */}
          </div>
        </td>
      </tr>
      {isOpen && children.map((child) => (
        <TreeRow key={child.id} row={child} depth={depth + 1} rowsIndex={rowsIndex} expanded={expanded} onToggle={onToggle} onAction={onAction} highlightId={highlightId} />
      ))}
    </>
  );
}

function HierarchyTreeTable({ rootRows, rowsIndex, onAction, highlightId, expandedIds }) {
  const [expanded, setExpanded] = useState(() => new Set(expandedIds || []));
  useEffect(() => { if (expandedIds) setExpanded(new Set(expandedIds)); }, [expandedIds]);
  const toggle = (id) => setExpanded((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

  return (
    <div className="overflow-auto border border-border rounded-xl max-h-[calc(100vh-200px)]">
      <table className="w-full text-left border-collapse min-w-[980px]">
        <thead className="sticky top-0 z-10 bg-muted">
          <tr className="border-b border-border">
            {['Name', 'Code / ID', 'Device Type', 'Children / Devices', 'Coordinates', 'Polygon / Coord Status', 'Status', 'Actions'].map((h) => (
              <th key={h} className="px-2 py-2 text-[9px] uppercase tracking-widest font-bold text-left">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rootRows.map((row) => (
            <TreeRow key={row.id} row={row} depth={0} rowsIndex={rowsIndex} expanded={expanded} onToggle={toggle} onAction={onAction} highlightId={highlightId} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ============================================================================
 * SECTION 5 — CASCADING ADD / EDIT FORM
 * ==========================================================================*/

const LEVEL_ORDER = ['country', 'city', 'ops', 'location', 'gate', 'device'];
const LEVEL_LABEL = { country: 'Country', city: 'City', ops: 'Ops', location: 'Location', gate: 'Gate', device: 'Device' };
const PARENT_CHAIN = {
  country: [], city: ['country'], ops: ['country', 'city'],
  location: ['country', 'city', 'ops'], gate: ['country', 'city', 'ops', 'location'],
  device: ['country', 'city', 'ops', 'location', 'gate'],
};

function optionsForLevel(level, selected, rootRows, rowsIndex) {
  if (level === 'country') return rootRows.filter((r) => r.level === 'COUNTRY');
  const parentLevel = PARENT_CHAIN[level][PARENT_CHAIN[level].length - 1];
  const parentId = selected[parentLevel];
  if (!parentId) return [];
  return (rowsIndex.get(parentId) || []).filter((r) => r.level === level.toUpperCase());
}

function TextField({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</label>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground" required />
    </div>
  );
}

function CascadingEntityForm({ entityType, presetParent, editingRow, rootRows, rowsIndex, onCancel, onSubmit }) {
  const chain = PARENT_CHAIN[entityType];
  const [selected, setSelected] = useState(() => {
    const init = {};
    if (presetParent) { if (presetParent.ancestors) Object.assign(init, presetParent.ancestors); init[presetParent.level] = presetParent.id; }
    return init;
  });
  const [fields, setFields] = useState(() => editingRow?.raw || {});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const allFilled = chain.every((lvl) => !!selected[lvl]);

  const handleSelect = (level, id) => {
    setSelected((prev) => {
      const next = { ...prev, [level]: id };
      LEVEL_ORDER.slice(LEVEL_ORDER.indexOf(level) + 1).forEach((l) => delete next[l]);
      return next;
    });
  };
  const setField = (key, value) => setFields((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true); setError(null);
    try { await onSubmit(entityType, { ...fields, parents: selected }); }
    catch (err) { setError(err?.message || 'Failed to save'); }
    finally { setSubmitting(false); }
  };

  const detailFields = () => {
    switch (entityType) {
      case 'country': return (<><TextField label="Country Name" value={fields.country_name || ''} onChange={(v) => setField('country_name', v)} /><TextField label="Country Code" value={fields.country || ''} onChange={(v) => setField('country', v)} /></>);
      case 'city': return (<><TextField label="City Name" value={fields.city_name || ''} onChange={(v) => setField('city_name', v)} /><TextField label="City Code" value={fields.city_cd || ''} onChange={(v) => setField('city_cd', v)} /></>);
      case 'ops': return (<><TextField label="Ops Name" value={fields.ops_name || ''} onChange={(v) => setField('ops_name', v)} /><TextField label="Ops Code" value={fields.ops || ''} onChange={(v) => setField('ops', v)} /></>);
      case 'location': return <TextField label="Location Name" value={fields.location || ''} onChange={(v) => setField('location', v)} />;
      case 'gate': return (<><TextField label="Gate Name" value={fields.gate_name || ''} onChange={(v) => setField('gate_name', v)} /><TextField label="Gate ID" value={fields.gate_id || ''} onChange={(v) => setField('gate_id', v)} /></>);
      case 'device': return (
        <>
          <TextField label="Device Name" value={fields.device_name || ''} onChange={(v) => setField('device_name', v)} />
          <TextField label="Device ID" value={fields.device_id || ''} onChange={(v) => setField('device_id', v)} />
          <TextField label="Device Type" value={fields.device_type || ''} onChange={(v) => setField('device_type', v)} placeholder="EXIT, APM, APP, HHD, POS..." />
          <div className="grid grid-cols-2 gap-2">
            <TextField label="Latitude" value={fields.latitude ?? ''} onChange={(v) => setField('latitude', v)} type="number" />
            <TextField label="Longitude" value={fields.longitude ?? ''} onChange={(v) => setField('longitude', v)} type="number" />
          </div>
        </>
      );
      default: return null;
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="w-[95vw] max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{editingRow ? `Edit ${LEVEL_LABEL[entityType]}` : `Add ${LEVEL_LABEL[entityType]}`}</DialogTitle>
          <DialogDescription>
            {editingRow ? `Update ${LEVEL_LABEL[entityType].toLowerCase()} details below.` : `Fill in the details to add a new ${LEVEL_LABEL[entityType].toLowerCase()}.`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-3 py-4 max-h-[60vh] overflow-y-auto pr-1">
            {chain.map((lvl) => {
              const opts = optionsForLevel(lvl, selected, rootRows, rowsIndex);
              const disabled = !!presetParent && !!selected[lvl] && LEVEL_ORDER.indexOf(lvl) <= LEVEL_ORDER.indexOf(presetParent.level);
              return (
                <div key={lvl}>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{LEVEL_LABEL[lvl]}</label>
                  <select
                    className="w-full mt-1 px-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground disabled:opacity-60"
                    value={selected[lvl] || ''}
                    disabled={disabled || (lvl !== 'country' && !selected[LEVEL_ORDER[LEVEL_ORDER.indexOf(lvl) - 1]])}
                    onChange={(e) => handleSelect(lvl, e.target.value)}
                    required
                  >
                    <option value="">Select {LEVEL_LABEL[lvl]}...</option>
                    {opts.map((o) => <option key={o.id} value={o.id}>{o.name}{o.code ? ` (${o.code})` : ''}</option>)}
                  </select>
                </div>
              );
            })}
            {allFilled && <div className="pt-2 border-t border-border/60 space-y-3">{detailFields()}</div>}
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={onCancel} className="w-full sm:w-auto">Cancel</Button>
            <Button type="submit" disabled={!allFilled || submitting} className="w-full sm:w-auto">
              {submitting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
              ) : editingRow ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================================
 * SECTION 6 — FILTER SELECT
 * ==========================================================================*/

const COMPACT_SELECT_STYLES = {
  control: (base) => ({ ...base, minHeight: 30, height: 30, fontSize: '12px' }),
  valueContainer: (base) => ({ ...base, height: 30, padding: '0 6px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: 30 }),
  indicatorSeparator: (base) => ({ ...base, marginTop: 6, marginBottom: 6 }),
  clearIndicator: (base) => ({ ...base, padding: 4 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  option: (base) => ({ ...base, fontSize: '12px', padding: '4px 8px' }),
  singleValue: (base) => ({ ...base, fontSize: '12px' }),
  placeholder: (base) => ({ ...base, fontSize: '12px' }),
  menu: (base) => ({ ...base, fontSize: '12px' }),
};

function FilterSelect({ label, value, onChange, options, placeholder = 'All' }) {
  const selectOptions = options.map((o) => ({ value: o.id, label: o.name }));
  return (
    <div className="min-w-[170px]">
      <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold block mb-1">{label}</label>
      <ReactSelect
        options={selectOptions}
        value={selectOptions.find((o) => o.value === value) || null}
        onChange={(s) => onChange(s?.value || '')}
        placeholder={placeholder}
        isClearable
        styles={COMPACT_SELECT_STYLES}
      />
    </div>
  );
}

const ADD_ENTITY_OPTIONS = [
  { type: 'country', label: 'Country' }, { type: 'city', label: 'City' }, { type: 'ops', label: 'Ops' },
  { type: 'location', label: 'Location' }, { type: 'gate', label: 'Gate' }, { type: 'device', label: 'Device' },
];

const POLYGON_STATUS_OPTIONS = [
  { value: 'Polygon Set', label: 'Polygon Set' },
  { value: 'Polygon Not Set', label: 'Polygon Not Set' },
];

/* ============================================================================
 * SECTION 6.5 — CLASSIC FLAT TABLE
 * ==========================================================================*/

const flattenOffStreetRows = (topLevel = []) => {
  const rows = [];
  getTopLevelCountries(topLevel).forEach((countryObj) => {
    getCountryOps(countryObj).forEach((op) => {
      (op.locations || []).forEach((loc) => {
        (loc.gates || []).forEach((gate) => {
          (gate.devices || []).forEach((device) => {
            rows.push({
              country: countryObj.country,
              country_name: countryObj.country_name,
              city_cd: op.city_cd,
              city_name: op.city_name,
              ops: op.ops,
              ops_name: op.ops_name,
              location: loc.location,
              gate_id: gate.gate_id,
              gate_name: gate.gate_name,
              device_id: device.device_id,
              device_name: device.device_name,
              device_type: device.device_type || null,
              latitude: device.latitude,
              longitude: device.longitude,
              row_id: device.row_id ?? null,
            });
          });
        });
      });
    });
  });
  return rows;
};

const ClassicFlatTable = () => {
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [sortBy, setSortBy] = useState('ops_name');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const hasMounted = useRef(false);

  // ── NEW: Duplicate device dialog state ──
  const [duplicateModal, setDuplicateModal] = useState(null); // { row, device_id, device_name, device_type, latitude, longitude }
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);
  const [customDeviceTypes, setCustomDeviceTypes] = useState([]);
  const [isAddingDeviceType, setIsAddingDeviceType] = useState(false);
  const [newDeviceTypeInput, setNewDeviceTypeInput] = useState('');
  const skipNextDeviceTypeBlurRef = useRef(false);

  // ── NEW: Create device dialog state (same create flow as PolygonMapEditor) ──
  const [createModal, setCreateModal] = useState(null); // { country, city_cd, ops, location, gate_id, gate_name, device_id, device_name, device_type, latitude, longitude }
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  // ── NEW: Delete confirmation dialog state ──
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { row }
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const res = await apiCall('get', '/cp-map/off-street', null, null, false, false);
      if (res?.success && res?.fields?.data) {
        setAllRows(flattenOffStreetRows(res.fields.data));
      } else {
        setAllRows([]);
      }
    } catch (err) {
      console.error('Failed to load records:', err);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    fetchRecords();
  }, []);

  const filteredRows = useMemo(() => {
    let rows = allRows;
    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          String(r.location || '').toLowerCase().includes(q) ||
          String(r.ops_name || '').toLowerCase().includes(q) ||
          String(r.city_name || '').toLowerCase().includes(q) ||
          String(r.device_id || '').toLowerCase().includes(q) ||
          String(r.device_name || '').toLowerCase().includes(q)
      );
    }
    if (sortBy) {
      rows = [...rows].sort((a, b) => {
        const av = a[sortBy] ?? '';
        const bv = b[sortBy] ?? '';
        const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
        return sortOrder === 'ASC' ? cmp : -cmp;
      });
    }
    return rows;
  }, [allRows, debouncedSearch, sortBy, sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortBy, sortOrder]);

  const totalRecords = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / perPage));
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredRows.slice(start, start + perPage);
  }, [filteredRows, currentPage, perPage]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder((p) => (p === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(col);
      setSortOrder('ASC');
    }
  };

  const deviceTypeOptions = useMemo(() => {
    const types = new Set(allRows.map((r) => r.device_type).filter(Boolean));
    customDeviceTypes.forEach((t) => types.add(t));
    return Array.from(types).sort();
  }, [allRows, customDeviceTypes]);

  const commitNewDeviceType = useCallback(() => {
    const newType = newDeviceTypeInput.trim();
    if (newType) {
      setCustomDeviceTypes((p) => (p.includes(newType) ? p : [...p, newType]));
      setDuplicateModal((p) => (p ? { ...p, device_type: newType } : p));
      setCreateModal((p) => (p ? { ...p, device_type: newType } : p));
    }
    setIsAddingDeviceType(false);
    setNewDeviceTypeInput('');
  }, [newDeviceTypeInput]);

  // ── NEW: cascading Country/City/Ops/Location/Gate options for the Create Device modal ──
  const countryOptions = useMemo(() => {
    const map = new Map();
    allRows.forEach((r) => { if (r.country && !map.has(r.country)) map.set(r.country, r.country_name || r.country); });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [allRows]);

  const cityOptions = useMemo(() => {
    const map = new Map();
    allRows.forEach((r) => {
      if (!r.city_cd) return;
      if (createModal?.country && r.country !== createModal.country) return;
      if (!map.has(r.city_cd)) map.set(r.city_cd, r.city_name || r.city_cd);
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [allRows, createModal?.country]);

  const opsOptions = useMemo(() => {
    const map = new Map();
    allRows.forEach((r) => {
      if (!r.ops) return;
      if (createModal?.country && r.country !== createModal.country) return;
      if (createModal?.city_cd && r.city_cd !== createModal.city_cd) return;
      if (!map.has(r.ops)) map.set(r.ops, r.ops_name || r.ops);
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [allRows, createModal?.country, createModal?.city_cd]);

  const locationOptions = useMemo(() => {
    const set = new Set();
    allRows.forEach((r) => {
      if (!r.location) return;
      if (createModal?.ops && r.ops !== createModal.ops) return;
      set.add(r.location);
    });
    return Array.from(set).map((v) => ({ value: v, label: v }));
  }, [allRows, createModal?.ops]);

  const gateOptions = useMemo(() => {
    const map = new Map();
    allRows.forEach((r) => {
      if (!r.gate_id) return;
      if (createModal?.location && r.location !== createModal.location) return;
      if (createModal?.ops && r.ops !== createModal.ops) return;
      if (!map.has(r.gate_id)) map.set(r.gate_id, r.gate_name || r.gate_id);
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [allRows, createModal?.location, createModal?.ops]);

  // ── NEW: Create device (same API/flow as PolygonMapEditor's "create" mode) ──
  const openCreateModal = () => {
    setCreateError(null);
    setIsAddingDeviceType(false);
    setNewDeviceTypeInput('');
    setCreateModal({
      country: '', city_cd: '', ops: '', location: '', gate_id: '', gate_name: '',
      device_id: '', device_name: '', device_type: '', latitude: '', longitude: '',
    });
  };

  const handleCreateSubmit = async () => {
    if (!createModal) return;
    if (!createModal.country || !createModal.city_cd || !createModal.ops || !createModal.location || !createModal.gate_id) {
      setCreateError('Please select Country, City, Ops, Location and Gate.');
      return;
    }
    if (!createModal.device_id?.trim()) { setCreateError('Device ID is required.'); return; }
    setCreating(true); setCreateError(null);
    try {
      const payload = {
        country: createModal.country,
        city_cd: createModal.city_cd,
        ops: createModal.ops,
        location: createModal.location,
        gate: createModal.gate_id,
        gate_name: createModal.gate_name,
        device_id: createModal.device_id.trim(),
        device_name: createModal.device_name,
        device_type: createModal.device_type,
        latitude: createModal.latitude,
        longitude: createModal.longitude,
      };
      await createDeviceApi(payload);
      setCreateModal(null);
      await fetchRecords();
    } catch (err) {
      setCreateError(err?.message || 'Failed to create device');
    } finally {
      setCreating(false);
    }
  };

  // ── NEW: Duplicate device (same API as PolygonMapEditor's duplicate flow) ──
  const openDuplicateModal = (row) => {
    setDuplicateError(null);
    setIsAddingDeviceType(false);
    setNewDeviceTypeInput('');
    setDuplicateModal({
      row,
      device_id: '',
      device_name: row.device_name ?? '',
      device_type: row.device_type ?? '',
      latitude: row.latitude ?? '',
      longitude: row.longitude ?? '',
    });
  };

  const handleDuplicateSubmit = async () => {
    if (!duplicateModal) return;
    if (!duplicateModal.device_id?.trim()) { setDuplicateError('Device ID is required.'); return; }
    setDuplicating(true); setDuplicateError(null);
    try {
      const payload = {
        row_id: duplicateModal.row.row_id,
        device_id: duplicateModal.device_id.trim(),
        device_name: duplicateModal.device_name,
        device_type: duplicateModal.device_type,
        latitude: duplicateModal.latitude !== '' ? Number(duplicateModal.latitude).toFixed(6) : duplicateModal.latitude,
        longitude: duplicateModal.longitude !== '' ? Number(duplicateModal.longitude).toFixed(6) : duplicateModal.longitude,
      };
      await duplicateDeviceApi(payload);
      setDuplicateModal(null);
      await fetchRecords();
    } catch (err) {
      setDuplicateError(err?.message || 'Failed to duplicate device');
    } finally {
      setDuplicating(false);
    }
  };

  // ── NEW: Delete device (same API as Hierarchy View) ──
  const openDeleteConfirm = (row) => { setDeleteError(null); setDeleteConfirm({ row }); };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true); setDeleteError(null);
    try {
      await deleteDeviceApi(deleteConfirm.row.row_id);
      setDeleteConfirm(null);
      await fetchRecords();
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete device');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { key: 'country', label: 'country', sortable: true },
    { key: 'country_name', label: 'country_name', sortable: true },
    { key: 'city_cd', label: 'city_cd', sortable: true },
    { key: 'city_name', label: 'city_name', sortable: true },
    { key: 'ops', label: 'ops', sortable: true },
    { key: 'ops_name', label: 'ops_name', sortable: true },
    { key: 'location', label: 'location', sortable: true },
    { key: 'gate_id', label: 'gate_id', sortable: true },
    { key: 'gate_name', label: 'gate_name', sortable: true },
    { key: 'device_id', label: 'device_id', sortable: true, render: (row) => <span className="font-medium">{row.device_id}</span> },
    { key: 'device_name', label: 'device_name', sortable: true },
    {
      key: 'device_type', label: 'device_type', sortable: true,
      render: (row) => {
        const color = getDeviceColor(row.device_type);
        return (
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            style={{ backgroundColor: `${color}1A`, color }}
          >
            {row.device_type || '—'}
          </span>
        );
      },
    },
    { key: 'latitude', label: 'latitude', sortable: true },
    { key: 'longitude', label: 'longitude', sortable: true },
  ];

  const rowActions = (row) => [
    // {
    //   label: 'Duplicate',
    //   icon: (
    //     <span className="flex items-center justify-center w-7 h-7 p-2 rounded-md bg-[#DBEAFE80] text-[#2563EB] dark:bg-[#DBEAFE15] dark:text-[#60A5FA]">
    //       <Copy className="h-4 w-4" />
    //     </span>
    //   ),
    //   onClick: () => openDuplicateModal(row),
    // },
    // {
    //   label: 'Delete',
    //   icon: (
    //     <span className="flex items-center justify-center w-7 h-7 p-2 rounded-md bg-red-500/10 text-red-500">
    //       <Trash2 className="h-4 w-4" />
    //     </span>
    //   ),
    //   onClick: () => openDeleteConfirm(row),
    // },
  ];

  return (
    <>
      <DataTable
        title="Off-Street Master Data"
        description="View parking sensor street data"
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search by location, ops, city or device..."
        headerActions={
          <Button size="sm" className="h-9 gap-1.5" onClick={openCreateModal}>
            <Plus className="h-4 w-4" />
            Add New Device
          </Button>
        }
        columns={columns}
        rows={pagedRows}
        rowKey="device_id"
        // rowActions={rowActions}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        pagination={{ currentPage, perPage, totalRecords, totalPages }}
        onPageChange={(p) => setCurrentPage(p)}
        onPerPageChange={(pp) => { setPerPage(Number(pp)); setCurrentPage(1); }}
        paginationTitle="records"
        loading={loading}
        emptyMessage="No street data found."
      />

      {/* ── NEW: Create device dialog (Country/City/Ops/Location/Gate + device fields, same create flow as PolygonMapEditor) ── */}
      {createModal && (
        <Dialog open onOpenChange={(open) => { if (!open && !creating) { setCreateModal(null); setCreateError(null); setIsAddingDeviceType(false); setNewDeviceTypeInput(''); } }}>
          <DialogContent className="w-[95vw] max-w-lg p-4">
            <DialogHeader className="pb-1">
              <DialogTitle className="text-base">Add Device</DialogTitle>
              <DialogDescription className="text-sm">
                Fill in the details for the new device. Since there's no row context here, pick the Country, City, Ops, Location and Gate it belongs to first.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 max-h-[75vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Country *</span>
                  <ReactSelect
                    placeholder="Select country"
                    controlHeight={32}
                    options={countryOptions}
                    value={createModal.country ? countryOptions.find((o) => o.value === createModal.country) || { value: createModal.country, label: createModal.country } : null}
                    onChange={(opt) => setCreateModal((p) => ({ ...p, country: opt?.value || '', city_cd: '', ops: '', location: '', gate_id: '', gate_name: '', latitude: '', longitude: '' }))}
                    isClearable
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">City *</span>
                  <ReactSelect
                    placeholder="Select city"
                    controlHeight={32}
                    options={cityOptions}
                    value={createModal.city_cd ? cityOptions.find((o) => o.value === createModal.city_cd) || { value: createModal.city_cd, label: createModal.city_cd } : null}
                    onChange={(opt) => setCreateModal((p) => ({ ...p, city_cd: opt?.value || '', ops: '', location: '', gate_id: '', gate_name: '', latitude: '', longitude: '' }))}
                    isClearable
                    isDisabled={!createModal.country}
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Ops *</span>
                  <ReactSelect
                    placeholder="Select ops"
                    controlHeight={32}
                    options={opsOptions}
                    value={createModal.ops ? opsOptions.find((o) => o.value === createModal.ops) || { value: createModal.ops, label: createModal.ops } : null}
                    onChange={(opt) => setCreateModal((p) => ({ ...p, ops: opt?.value || '', location: '', gate_id: '', gate_name: '', latitude: '', longitude: '' }))}
                    isClearable
                    isDisabled={!createModal.city_cd}
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Location *</span>
                  <ReactSelect
                    placeholder="Select location"
                    controlHeight={32}
                    options={locationOptions}
                    value={createModal.location ? locationOptions.find((o) => o.value === createModal.location) || { value: createModal.location, label: createModal.location } : null}
                    onChange={(opt) => setCreateModal((p) => ({ ...p, location: opt?.value || '', gate_id: '', gate_name: '', latitude: '', longitude: '' }))}
                    isClearable
                    isDisabled={!createModal.ops}
                  />
                </label>
              </div>
              <label className="block">
                <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Gate *</span>
                <ReactSelect
                  placeholder="Select gate"
                  controlHeight={32}
                  options={gateOptions}
                  value={createModal.gate_id ? gateOptions.find((o) => o.value === createModal.gate_id) || { value: createModal.gate_id, label: createModal.gate_id } : null}
                  onChange={(opt) => {
                    const gateId = opt?.value || '';
                    const gateName = opt?.label || '';
                    // auto-fill lat/long from an existing device already sitting on this gate
                    const match = gateId
                      ? allRows.find((r) => r.gate_id === gateId && r.location === createModal.location && r.ops === createModal.ops && r.latitude && r.longitude)
                      : null;
                    setCreateModal((p) => ({
                      ...p,
                      gate_id: gateId,
                      gate_name: gateName,
                      latitude: match ? match.latitude : '',
                      longitude: match ? match.longitude : '',
                    }));
                  }}
                  isClearable
                  isDisabled={!createModal.location}
                />
              </label>

              <div className="border-t pt-3 mt-1 space-y-3">
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Device ID *</span>
                  <input
                    className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    value={createModal.device_id}
                    onChange={(e) => setCreateModal((p) => ({ ...p, device_id: e.target.value }))}
                    placeholder="Enter device ID"
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Device Name</span>
                  <input
                    className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    value={createModal.device_name}
                    onChange={(e) => setCreateModal((p) => ({ ...p, device_name: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Device Type</span>
                  {isAddingDeviceType ? (
                    <input
                      autoFocus
                      className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder="Type new device type"
                      value={newDeviceTypeInput}
                      onChange={(e) => setNewDeviceTypeInput(e.target.value)}
                      onBlur={() => {
                        if (skipNextDeviceTypeBlurRef.current) { skipNextDeviceTypeBlurRef.current = false; return; }
                        commitNewDeviceType();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          skipNextDeviceTypeBlurRef.current = true;
                          commitNewDeviceType();
                        } else if (e.key === 'Escape') {
                          skipNextDeviceTypeBlurRef.current = true;
                          setIsAddingDeviceType(false);
                          setNewDeviceTypeInput('');
                        }
                      }}
                    />
                  ) : (
                    <ReactSelect
                      placeholder="Select a device type"
                      controlHeight={32}
                      options={[
                        { value: '__ADD_NEW_DEVICE_TYPE__', label: <span className="text-[10px] font-semibold">+ Add New Type</span> },
                        ...deviceTypeOptions.map((t) => ({ value: t, label: t })),
                      ]}
                      value={createModal.device_type ? { value: createModal.device_type, label: createModal.device_type } : null}
                      onChange={(opt) => {
                        if (opt?.value === '__ADD_NEW_DEVICE_TYPE__') {
                          setIsAddingDeviceType(true);
                          setNewDeviceTypeInput('');
                          return;
                        }
                        setCreateModal((p) => ({ ...p, device_type: opt?.value ?? '' }));
                      }}
                    />
                  )}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block">
                    <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Latitude</span>
                    <input
                      className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      value={createModal.latitude}
                      onChange={(e) => setCreateModal((p) => ({ ...p, latitude: e.target.value }))}
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Longitude</span>
                    <input
                      className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      value={createModal.longitude}
                      onChange={(e) => setCreateModal((p) => ({ ...p, longitude: e.target.value }))}
                    />
                  </label>
                </div>
                <p className="text-[10px] text-muted-foreground -mt-1">
                  Auto-filled from an existing device on the selected gate when available — feel free to adjust.
                </p>
              </div>
            </div>

            {createError && <p className="text-[11px] text-red-500 mt-2">{createError}</p>}

            <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setCreateModal(null); setCreateError(null); setIsAddingDeviceType(false); setNewDeviceTypeInput(''); }}
                disabled={creating}
                className="w-full sm:w-auto h-8 text-sm"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleCreateSubmit}
                disabled={creating}
                className="w-full sm:w-auto h-8 text-sm"
              >
                {creating ? (
                  <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Creating...</>
                ) : (
                  <><Plus className="mr-2 h-3.5 w-3.5" />Create Device</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── NEW: Duplicate device dialog ── */}
      {duplicateModal && (
        <Dialog open onOpenChange={(open) => { if (!open && !duplicating) { setDuplicateModal(null); setDuplicateError(null); setIsAddingDeviceType(false); setNewDeviceTypeInput(''); } }}>
          <DialogContent className="w-[95vw] max-w-md p-4">
            <DialogHeader className="pb-1">
              <DialogTitle className="text-base">Duplicate Device</DialogTitle>
              <DialogDescription className="text-sm">
                Duplicating <strong>{duplicateModal.row.device_name || duplicateModal.row.device_id}</strong> — review the fields below. The new device needs its own Device ID.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <label className="block">
                <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Device ID *</span>
                <input
                  autoFocus
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  value={duplicateModal.device_id}
                  onChange={(e) => setDuplicateModal((p) => ({ ...p, device_id: e.target.value }))}
                  placeholder="Enter new device ID"
                />
              </label>
              <label className="block">
                <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Device Name</span>
                <input
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  value={duplicateModal.device_name}
                  onChange={(e) => setDuplicateModal((p) => ({ ...p, device_name: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Device Type</span>
                {isAddingDeviceType ? (
                  <input
                    autoFocus
                    className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="Type new device type"
                    value={newDeviceTypeInput}
                    onChange={(e) => setNewDeviceTypeInput(e.target.value)}
                    onBlur={() => {
                      if (skipNextDeviceTypeBlurRef.current) { skipNextDeviceTypeBlurRef.current = false; return; }
                      commitNewDeviceType();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        skipNextDeviceTypeBlurRef.current = true;
                        commitNewDeviceType();
                      } else if (e.key === 'Escape') {
                        skipNextDeviceTypeBlurRef.current = true;
                        setIsAddingDeviceType(false);
                        setNewDeviceTypeInput('');
                      }
                    }}
                  />
                ) : (
                  <ReactSelect
                    placeholder="Select a device type"
                    controlHeight={32}
                    options={[
                      { value: '__ADD_NEW_DEVICE_TYPE__', label: <span className="text-[10px] font-semibold">+ Add New Type</span> },
                      ...deviceTypeOptions.map((t) => ({ value: t, label: t })),
                    ]}
                    value={duplicateModal.device_type ? { value: duplicateModal.device_type, label: duplicateModal.device_type } : null}
                    onChange={(opt) => {
                      if (opt?.value === '__ADD_NEW_DEVICE_TYPE__') {
                        setIsAddingDeviceType(true);
                        setNewDeviceTypeInput('');
                        return;
                      }
                      setDuplicateModal((p) => ({ ...p, device_type: opt?.value ?? '' }));
                    }}
                  />
                )}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Latitude</span>
                  <input
                    className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    value={duplicateModal.latitude}
                    onChange={(e) => setDuplicateModal((p) => ({ ...p, latitude: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Longitude</span>
                  <input
                    className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    value={duplicateModal.longitude}
                    onChange={(e) => setDuplicateModal((p) => ({ ...p, longitude: e.target.value }))}
                  />
                </label>
              </div>
            </div>

            {duplicateError && <p className="text-[11px] text-red-500 mt-2">{duplicateError}</p>}

            <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setDuplicateModal(null); setDuplicateError(null); setIsAddingDeviceType(false); setNewDeviceTypeInput(''); }}
                disabled={duplicating}
                className="w-full sm:w-auto h-8 text-sm"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleDuplicateSubmit}
                disabled={duplicating}
                className="w-full sm:w-auto h-8 text-sm"
              >
                {duplicating ? (
                  <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Duplicating...</>
                ) : (
                  <><Copy className="mr-2 h-3.5 w-3.5" />Duplicate Device</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── NEW: Delete confirmation dialog ── */}
      {deleteConfirm && (
        <Dialog open onOpenChange={(open) => { if (!open && !deleting) { setDeleteConfirm(null); setDeleteError(null); } }}>
          <DialogContent className="w-[95vw] max-w-md p-4">
            <DialogHeader className="pb-1">
              <DialogTitle className="text-destructive text-base">Delete Device</DialogTitle>
              <DialogDescription className="text-sm">
                Are you sure you want to delete this device? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <div className="py-2 px-3 border rounded-lg bg-muted/30">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-xs text-muted-foreground">Name</span>
                  <span className="text-sm font-medium truncate max-w-[220px]">{deleteConfirm.row.device_name || '—'}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-xs text-muted-foreground">Device ID</span>
                  <span className="text-sm truncate max-w-[220px]">{deleteConfirm.row.device_id || '—'}</span>
                </div>
                <div className="flex items-center justify-between gap-4 pt-1 border-t">
                  <span className="text-xs text-muted-foreground">Device Type</span>
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
                    style={{
                      backgroundColor: `${getDeviceColor(deleteConfirm.row.device_type)}1A`,
                      color: getDeviceColor(deleteConfirm.row.device_type),
                    }}
                  >
                    {getDeviceTypeName(deleteConfirm.row.device_type)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-xs text-muted-foreground">Coordinates</span>
                  <span className="text-sm">
                    {deleteConfirm.row.latitude && deleteConfirm.row.longitude
                      ? `${deleteConfirm.row.latitude}, ${deleteConfirm.row.longitude}`
                      : '—'}
                  </span>
                </div>
              </div>
            </div>

            {deleteError && (
              <p className="text-[11px] text-red-500 mt-2">{deleteError}</p>
            )}

            <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setDeleteConfirm(null); setDeleteError(null); }}
                disabled={deleting}
                className="w-full sm:w-auto h-8 text-sm"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                className="w-full sm:w-auto bg-destructive text-white hover:bg-destructive/90 h-8 text-sm"
              >
                {deleting ? (
                  <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Deleting...</>
                ) : (
                  <><Trash2 className="mr-2 h-3.5 w-3.5" />Delete Device</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

/* ============================================================================
 * SECTION 7 — MAIN PAGE
 * ==========================================================================*/

const HierarchyView = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ country: '', city: '', ops: '', location: '', gate: '', deviceType: '', polygonStatus: '' });
  const [showFilters, setShowFilters] = useState(false);

  const [formState, setFormState] = useState(null);
  const [mapState, setMapState] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [expandLevel, setExpandLevel] = useState('CITY');

  // ── NEW: Delete confirmation dialog state (replaces window.confirm) ──
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { row }
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setRawData(await fetchHierarchyData()); }
    catch (err) { setError(err?.message || 'Failed to load hierarchy'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const allRows = useMemo(() => normalizeHierarchy(rawData), [rawData]);
  const rowsIndex = useMemo(() => indexByParent(allRows), [allRows]);
  const rowsById = useMemo(() => new Map(allRows.map((r) => [r.id, r])), [allRows]);
  const rootRows = useMemo(() => rowsIndex.get('__root__') || [], [rowsIndex]);

  useEffect(() => {
    if (!mapState) return;
    const fresh = rowsById.get(mapState.row.id);
    if (fresh && fresh !== mapState.row) {
      setMapState((prev) => (prev ? { ...prev, row: fresh } : prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsById]);

  const expandToLevel = useCallback((targetLevel) => {
    const targetIdx = HIERARCHY_LEVEL_ORDER.indexOf(targetLevel);
    const ids = allRows
      .filter((r) => HIERARCHY_LEVEL_ORDER.indexOf(r.level) < targetIdx && rowsIndex.has(r.id))
      .map((r) => r.id);
    setExpandedIds(new Set(ids));
    setExpandLevel(targetLevel);
  }, [allRows, rowsIndex]);

  useEffect(() => {
    if (!rootRows.length) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      rootRows.forEach((r) => next.add(r.id));
      return next;
    });
  }, [rootRows]);

  const availableCountries = useMemo(() => allRows.filter((r) => r.level === 'COUNTRY'), [allRows]);

  const availableCities = useMemo(
    () => allRows.filter((r) => r.level === 'CITY' && (!filters.country || r.parentId === filters.country)),
    [allRows, filters.country]
  );

  const availableOps = useMemo(
    () =>
      allRows.filter((r) => {
        if (r.level !== 'OPS') return false;
        if (filters.city) return r.parentId === filters.city;
        if (filters.country) return r.parentId === filters.country || r.parentId?.startsWith(`${filters.country}/`);
        return true;
      }),
    [allRows, filters.country, filters.city]
  );

  const availableLocations = useMemo(
    () =>
      allRows.filter((r) => {
        if (r.level !== 'LOCATION') return false;
        if (filters.ops) return r.parentId === filters.ops;
        if (filters.city) return r.parentId?.startsWith(`${filters.city}/`);
        if (filters.country) return r.parentId?.startsWith(`${filters.country}/`);
        return true;
      }),
    [allRows, filters.country, filters.city, filters.ops]
  );

  const availableGates = useMemo(
    () =>
      allRows.filter((r) => {
        if (r.level !== 'GATE') return false;
        if (filters.location) return r.parentId === filters.location;
        if (filters.ops) return r.parentId?.startsWith(`${filters.ops}/`);
        if (filters.city) return r.parentId?.startsWith(`${filters.city}/`);
        if (filters.country) return r.parentId?.startsWith(`${filters.country}/`);
        return true;
      }),
    [allRows, filters.country, filters.city, filters.ops, filters.location]
  );

  const availableDeviceTypes = useMemo(() => {
    const scopeId = filters.gate || filters.location || filters.ops || filters.city || filters.country || null;
    const set = new Set();
    allRows.forEach((r) => {
      if (r.level !== 'DEVICE' || !r.deviceType) return;
      if (!scopeId || r.id === scopeId || r.id.startsWith(`${scopeId}/`)) set.add(r.deviceType);
    });
    return Array.from(set).sort();
  }, [allRows, filters.country, filters.city, filters.ops, filters.location, filters.gate]);

  const searchMatchRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return null;
    return allRows.filter((r) => String(r.name || '').toLowerCase().includes(q) || String(r.code || '').toLowerCase().includes(q) || String(r.deviceType || '').toLowerCase().includes(q));
  }, [allRows, searchTerm]);

  useEffect(() => {
    if (!searchMatchRows || !searchMatchRows.length) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      searchMatchRows.forEach((match) => buildBreadcrumb(match, rowsById).forEach((c) => next.add(c.id)));
      return next;
    });
    setHighlightId(searchMatchRows[0]?.id || null);
    const t = setTimeout(() => {
      const el = document.getElementById(`hrow-${searchMatchRows[0]?.id}`);
      if (!el) return;
      const container = el.closest('.overflow-auto');
      if (!container) { el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); return; }
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const delta = (elRect.top + elRect.bottom) / 2 - (containerRect.top + containerRect.bottom) / 2;
      container.scrollBy({ top: delta, behavior: 'smooth' });
    }, 150);
    return () => clearTimeout(t);
  }, [searchMatchRows, rowsById]);

  const idIsOnLine = (filterId, id) =>
    id === filterId || id.startsWith(`${filterId}/`) || filterId.startsWith(`${id}/`);

  const buildLeafFilterIds = useCallback(
    (predicate) => {
      const matched = allRows.filter(predicate);
      if (!matched.length) return new Set();
      const keep = new Set();
      matched.forEach((row) => {
        keep.add(row.id);
        let parentId = row.parentId;
        while (parentId) {
          keep.add(parentId);
          parentId = rowsById.get(parentId)?.parentId;
        }
      });
      allRows.forEach((row) => {
        if (matched.some((m) => row.id.startsWith(`${m.id}/`))) keep.add(row.id);
      });
      return keep;
    },
    [allRows, rowsById]
  );

  const deviceTypeIds = useMemo(
    () => (filters.deviceType ? buildLeafFilterIds((r) => r.level === 'DEVICE' && r.deviceType === filters.deviceType) : null),
    [filters.deviceType, buildLeafFilterIds]
  );
  const polygonStatusIds = useMemo(
    () => (filters.polygonStatus ? buildLeafFilterIds((r) => r.polygonStatus === filters.polygonStatus) : null),
    [filters.polygonStatus, buildLeafFilterIds]
  );

  const filteredRowsIndex = useMemo(() => {
    const pathFilterIds = [filters.country, filters.city, filters.ops, filters.location, filters.gate].filter(Boolean);
    const hasAnyFilter = pathFilterIds.length || deviceTypeIds || polygonStatusIds;
    if (!hasAnyFilter) return rowsIndex;

    const kept = allRows.filter((row) => {
      if (!pathFilterIds.every((fid) => idIsOnLine(fid, row.id))) return false;
      if (deviceTypeIds && !deviceTypeIds.has(row.id)) return false;
      if (polygonStatusIds && !polygonStatusIds.has(row.id)) return false;
      return true;
    });
    return indexByParent(kept);
  }, [allRows, rowsIndex, filters.country, filters.city, filters.ops, filters.location, filters.gate, deviceTypeIds, polygonStatusIds]);

  const visibleRootRows = useMemo(() => filteredRowsIndex.get('__root__') || [], [filteredRowsIndex]);

  // ── NEW: auto-expand the tree down to whatever level the active filters reach ──
  useEffect(() => {
    const hasPathFilter = filters.country || filters.city || filters.ops || filters.location || filters.gate;
    if (!hasPathFilter) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      allRows.forEach((row) => { if (filteredRowsIndex.has(row.id)) next.add(row.id); });
      return next;
    });
  }, [filters.country, filters.city, filters.ops, filters.location, filters.gate, filteredRowsIndex, allRows]);

  const handleAction = useCallback((action, row) => {
    switch (action) {
      case 'view': setHighlightId(row.id); break;
      case 'edit': setFormState({ entityType: row.level.toLowerCase(), editingRow: row }); break;
      case 'addChild': {
        const childTypeMap = { COUNTRY: 'city', CITY: 'ops', OPS: 'location', LOCATION: 'gate', GATE: 'device' };
        const entityType = childTypeMap[row.level];
        if (!entityType) return;
        const ancestors = {};
        buildBreadcrumb(row, rowsById).forEach((c) => { ancestors[c.level.toLowerCase()] = c.id; });
        setFormState({ entityType, presetParent: { level: row.level.toLowerCase(), id: row.id, ancestors } });
        break;
      }
      case 'polygon': setMapState({ row, mode: 'polygon' }); break;
      case 'coords': setMapState({ row, mode: 'coords' }); break;
      case 'delete':
        setDeleteError(null);
        setDeleteConfirm({ row });
        break;
      default: break;
    }
  }, [rowsById]);

  // ── NEW: Delete confirm → actual API call ──
  const confirmDelete = useCallback(async () => {
    if (!deleteConfirm) return;
    const { row } = deleteConfirm;
    setDeleting(true);
    setDeleteError(null);
    try {
      if (row.level === 'DEVICE') {
        await deleteDeviceApi(row.rowId);
      } else {
        await deleteEntityData(row.level.toLowerCase(), row.code || row.id);
      }
      setDeleteConfirm(null);
      await load();
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  }, [deleteConfirm, load]);

  const handleFormSubmit = async (entityType, payload) => {
    if (formState?.editingRow) await updateEntityData(entityType, formState.editingRow.code || formState.editingRow.id, payload);
    else await createEntity(entityType, payload);
    setFormState(null);
    await load();
  };

  const handleDeviceCoordsSave = async (row, coords) => {
    await updateDevicesCoordinatesData([{ rowId: row.rowId, latitude: coords.lat, longitude: coords.lng }]);
    setMapState(null);
    await load();
  };
  const handleDevicesCoordsSave = async (moves) => {
    await updateDevicesCoordinatesData(moves.map(({ row, coords }) => ({ rowId: row.rowId, latitude: coords.lat, longitude: coords.lng })));
  };
  const handlePolygonSaved = async () => {
    await load();
  };
  const handleDeviceDuplicated = async () => {
    await load();
  };
  const handleDeviceCreated = async () => {
    await load();
  };
  const handleDeviceDeleted = async () => {
    await load();
  };
  const handleChildPolygonSaved = async () => {
    await load();
  };
  const handleJumpTo = (childRow, forceMode) => {
    if (childRow.level === 'DEVICE') setMapState({ row: childRow, mode: 'coords' });
    else setMapState({ row: childRow, mode: forceMode || 'polygon' });
  };
  const handleBreadcrumbClick = (crumb) => {
    const row = rowsById.get(crumb.id);
    if (!row) return;
    setMapState({ row, mode: row.level === 'DEVICE' ? 'coords' : 'polygon' });
  };

  if (loading) return <LoadingComponent />;
  if (error) return <div className="p-8 text-sm text-red-500 flex items-center gap-3">{error}<button onClick={load} className="underline">Retry</button></div>;

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold text-foreground">Off-Street Hierarchy & Geographic Management</h1>
          <p className="text-xs text-muted-foreground">Country → City → Ops → Location → Gate → Device</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-9 gap-1.5">
                <ChevronDown className="h-3.5 w-3.5" />
                Expand: {EXPAND_LEVEL_OPTIONS.find((o) => o.level === expandLevel)?.label || 'City'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              <DropdownMenuLabel>Expand up to</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {EXPAND_LEVEL_OPTIONS.map((opt) => (
                <DropdownMenuItem key={opt.level} onClick={() => expandToLevel(opt.level)}>
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-9 gap-1.5">
                <ChevronRight className="h-3.5 w-3.5" /> Collapse
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              <DropdownMenuLabel>Collapse to</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLLAPSE_LEVEL_OPTIONS.map((opt) => (
                <DropdownMenuItem key={opt.level} onClick={() => expandToLevel(opt.level)}>
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 flex-wrap">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search hierarchy..."
            className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-border bg-background text-foreground" />
        </div>
        <button onClick={() => setShowFilters((v) => !v)} className={`p-2 rounded-lg border text-xs font-semibold flex items-center gap-1.5 flex-shrink-0 ${showFilters ? 'bg-primary/10 border-primary/40 text-primary' : 'border-border text-muted-foreground hover:bg-secondary'}`}>
          <SlidersHorizontal className="w-3.5 h-3.5" /> Filters
        </button>
      </div>

      {showFilters && (
        <div className="flex items-center gap-2 flex-wrap p-3 rounded-xl border border-border bg-card">
          <FilterSelect label="Country" value={filters.country} onChange={(v) => setFilters((f) => ({ ...f, country: v, city: '', ops: '', location: '', gate: '' }))} options={availableCountries} />
          <FilterSelect label="City" value={filters.city} onChange={(v) => setFilters((f) => ({ ...f, city: v, ops: '', location: '', gate: '' }))} options={availableCities} />
          <FilterSelect label="Ops" value={filters.ops} onChange={(v) => setFilters((f) => ({ ...f, ops: v, location: '', gate: '' }))} options={availableOps} />
          <FilterSelect label="Location" value={filters.location} onChange={(v) => setFilters((f) => ({ ...f, location: v, gate: '' }))} options={availableLocations} />
          <FilterSelect label="Gate" value={filters.gate} onChange={(v) => setFilters((f) => ({ ...f, gate: v }))} options={availableGates} />
          <div className="min-w-[170px]">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold block mb-1">Device Type</label>
            <ReactSelect
              options={availableDeviceTypes.map((t) => ({ value: t, label: t }))}
              value={filters.deviceType ? { value: filters.deviceType, label: filters.deviceType } : null}
              onChange={(s) => setFilters((f) => ({ ...f, deviceType: s?.value || '' }))}
              placeholder="All"
              isClearable
              styles={COMPACT_SELECT_STYLES}
            />
          </div>
        </div>
      )}

      <HierarchyTreeTable rootRows={visibleRootRows} rowsIndex={filteredRowsIndex} onAction={handleAction} highlightId={highlightId} expandedIds={expandedIds} />

      {formState && (
        <CascadingEntityForm
          entityType={formState.entityType}
          presetParent={formState.presetParent}
          editingRow={formState.editingRow}
          rootRows={rootRows}
          rowsIndex={rowsIndex}
          onCancel={() => setFormState(null)}
          onSubmit={handleFormSubmit}
        />
      )}

      {mapState && (
        <PolygonMapEditor
          row={mapState.row}
          rowsIndex={rowsIndex}
          polygon={mapState.row.raw?.coordinates || null}
          onDeviceSave={handleDeviceCoordsSave}
          onDevicesCoordsSave={handleDevicesCoordsSave}
          onDeviceDuplicated={handleDeviceDuplicated}
          onDeviceCreated={handleDeviceCreated}
          onDeviceDeleted={handleDeviceDeleted}
          onPolygonSaved={handlePolygonSaved}
          onChildPolygonSaved={handleChildPolygonSaved}
          onClose={() => setMapState(null)}
          onJumpTo={handleJumpTo}
          breadcrumb={buildBreadcrumb(mapState.row, rowsById)}
          onBreadcrumbClick={handleBreadcrumbClick}
          isDarkMode={isDarkMode}
        />
      )}

      {/* ── NEW: Delete confirmation dialog ── */}
      {deleteConfirm && (
        <Dialog open onOpenChange={(open) => { if (!open && !deleting) { setDeleteConfirm(null); setDeleteError(null); } }}>
          <DialogContent className="w-[95vw] max-w-md p-4">
            <DialogHeader className="pb-1">
              <DialogTitle className="text-destructive text-base">
                Delete {deleteConfirm.row.level.charAt(0) + deleteConfirm.row.level.slice(1).toLowerCase()}
              </DialogTitle>
              <DialogDescription className="text-sm">
                Are you sure you want to delete this {deleteConfirm.row.level.toLowerCase()}? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <div className="py-2 px-3 border rounded-lg bg-muted/30">
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-xs text-muted-foreground">Name</span>
                  <span className="text-sm font-medium truncate max-w-[220px]">{deleteConfirm.row.name || '—'}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-xs text-muted-foreground">Code / ID</span>
                  <span className="text-sm truncate max-w-[220px]">{deleteConfirm.row.code || '—'}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-xs text-muted-foreground">Level</span>
                  <span className="text-xs font-semibold uppercase tracking-wide bg-secondary px-2 py-0.5 rounded-full">
                    {deleteConfirm.row.level}
                  </span>
                </div>

                {deleteConfirm.row.level === 'DEVICE' && (
                  <>
                    <div className="flex items-center justify-between gap-4 pt-1 border-t">
                      <span className="text-xs text-muted-foreground">Device Type</span>
                      <span
                        className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
                        style={{
                          backgroundColor: `${getDeviceColor(deleteConfirm.row.deviceType)}1A`,
                          color: getDeviceColor(deleteConfirm.row.deviceType),
                        }}
                      >
                        {getDeviceTypeName(deleteConfirm.row.deviceType)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-xs text-muted-foreground">Coordinates</span>
                      <span className="text-sm">
                        {deleteConfirm.row.lat != null
                          ? `${deleteConfirm.row.lat.toFixed(5)}, ${deleteConfirm.row.lng.toFixed(5)}`
                          : '—'}
                      </span>
                    </div>
                  </>
                )}

                {deleteConfirm.row.level === 'GATE' && (
                  <div className="flex items-center justify-between gap-4 pt-1 border-t">
                    <span className="text-xs text-muted-foreground">Devices</span>
                    <span className="text-sm">{deleteConfirm.row.childCount ?? 0}</span>
                  </div>
                )}
              </div>
            </div>

            {deleteError && (
              <p className="text-[11px] text-red-500 mt-2">{deleteError}</p>
            )}

            <DialogFooter className="flex-col-reverse sm:flex-row gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setDeleteConfirm(null); setDeleteError(null); }}
                disabled={deleting}
                className="w-full sm:w-auto h-8 text-sm"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
                className="w-full sm:w-auto bg-destructive text-white hover:bg-destructive/90 h-8 text-sm"
              >
                {deleting ? (
                  <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Deleting...</>
                ) : (
                  <><Trash2 className="mr-2 h-3.5 w-3.5" />Delete {deleteConfirm.row.level.charAt(0) + deleteConfirm.row.level.slice(1).toLowerCase()}</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

/* ============================================================================
 * SECTION 8 — TABBED WRAPPER
 * ==========================================================================*/

const OffStreetMasterData = () => {
  const [activeTab, setActiveTab] = useState('hierarchy');

  const tabBtn = (key) =>
    `px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${
      activeTab === key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'
    }`;

  return (
    <div className="relative min-h-screen bg-background">
      <div className="absolute top-8 left-1/2 -translate-x-1/2 z-10">
        <div className="flex items-center gap-2 p-1 rounded-xl border border-border bg-card w-fit">
          <button className={tabBtn('hierarchy')} onClick={() => setActiveTab('hierarchy')}>Hierarchy View</button>
          <button className={tabBtn('classic')} onClick={() => setActiveTab('classic')}>Tabular View</button>
        </div>
      </div>

      <div className="">
        {activeTab === 'hierarchy' ? <HierarchyView /> : <ClassicFlatTable />}
      </div>
    </div>
  );
};

export default OffStreetMasterData;